import api from '../../lib/commons/api'
import {
  CreateVersionReportTemplatePageParams,
  VersionReportTemplateRepository,
} from '../../applications/ports/versionReportTemplateRepository'
import { useCallback } from 'react'
import {
  VersionReportTemplateEntity,
  VersionReportTemplatePageEntity,
  VersionReportPageConfig,
} from '../../domain/entity/version-report/VersionReportTemplateEntity'

type VersionReportTemplateResponse = {
  uuid: string
  name: string
  pages: {
    uuid: string
    pageTitle: string
    pageNumber: number
    pageType: string
    configJsonString: string
  }[]
  updatedAt: number
  updatedBy: string
}

const convertResponseToEntity = (
  response: VersionReportTemplateResponse
): VersionReportTemplateEntity => {
  return {
    uuid: response.uuid,
    name: response.name,
    pages: response.pages.map(page => ({
      uuid: page.uuid,
      pageTitle: page.pageTitle,
      pageNumber: page.pageNumber,
      pageType: page.pageType as VersionReportTemplatePageEntity['pageType'],
      config: JSON.parse(page.configJsonString) as VersionReportPageConfig,
    })),
    updatedAt: new Date(response.updatedAt),
    updatedBy: response.updatedBy,
  }
}

export const useVersionReportTemplateRepository =
  (): VersionReportTemplateRepository => {
    const fetchAll = useCallback(async () => {
      const response = await api.functional.request(
        'GET',
        '/api/v1/projects/version-report/template/list'
      )
      const templates: VersionReportTemplateResponse[] = response.json
      return templates.map(convertResponseToEntity)
    }, [])

    const create = useCallback(
      async (
        name: string,
        pages: CreateVersionReportTemplatePageParams[]
      ): Promise<{ uuid: string }> => {
        const request = {
          name,
          pages: pages.map(page => ({
            pageTitle: page.pageTitle,
            pageNumber: page.pageNumber,
            pageType: page.pageType,
            configJsonString: JSON.stringify(page.config),
          })),
        }
        const response = await api.functional.request(
          'POST',
          '/api/v1/projects/version-report/template',
          request
        )
        return response.json
      },
      []
    )
    const update = useCallback(
      async (newEntity: VersionReportTemplateEntity): Promise<void> => {
        const request = {
          uuid: newEntity.uuid,
          name: newEntity.name,
          pages: newEntity.pages.map(page => ({
            uuid: page.uuid,
            pageTitle: page.pageTitle,
            pageNumber: page.pageNumber,
            pageType: page.pageType,
            configJsonString: JSON.stringify(page.config),
          })),
        }
        await api.functional.request(
          'PUT',
          `/api/v1/projects/version-report/template/update`,
          request
        )
      },
      []
    )
    return {
      fetchAll,
      create,
      update,
    }
  }
